/**
 * @fileoverview Responsive utility functions for viewport and dimension calculations.
 * Handles responsive sizing and layout adjustments.
 */


/**
 * Dimension configuration.
 */
export interface Dimensions {
    width: number;
    height: number;
}

/**
 * Responsive utility class for viewport and dimension calculations.
 */
export class ResponsiveUtils {
    /**
     * Calculate responsive canvas dimensions based on viewport.
     * Uses 95% of viewport, max 800x600 for performance.
     * Minimum sizes: 400x300 for usability.
     * @returns Canvas dimensions
     */
    static getResponsiveDimensions(): Dimensions {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        
        // Use 95% of viewport, max 800x600 for performance
        let width = Math.min(windowWidth * 0.95, 800);
        let height = Math.min(windowHeight * 0.95, 600);
        
        // Minimum sizes for usability
        width = Math.max(width, 400);
        height = Math.max(height, 300);
        
        return { width: Math.floor(width), height: Math.floor(height) };
    }

    /**
     * Determine number of pairs based on screen size.
     * Currently always returns 3 pairs (adjusts size instead).
     * @returns Number of DS/TS pairs
     */
    static getNumberOfPairs(): number {
        // Always use 3 pairs, adjust size instead
        return 3;
    }

    /**
     * Calculate responsive title font size based on screen height.
     * Range: 24px to 36px.
     * @param height - Screen height in pixels
     * @returns Font size in pixels
     */
    static getResponsiveTitleFontSize(height: number): number {
        return Math.max(24, Math.min(36, Math.floor(height / 16)));
    }

    /**
     * Calculate responsive feedback font size based on screen height.
     * Range: 32px to 48px.
     * @param height - Screen height in pixels
     * @returns Font size in pixels
     */
    static getResponsiveFeedbackFontSize(height: number): number {
        return Math.max(32, Math.min(48, Math.floor(height / 12)));
    }

    /**
     * Calculate responsive number/letter font size based on screen height.
     * Range: 24px to 32px.
     * @param height - Screen height in pixels
     * @returns Font size in pixels
     */
    static getResponsiveNumberFontSize(height: number): number {
        return Math.max(24, Math.min(32, Math.floor(height / 18)));
    }

    /**
     * Calculate responsive mute button size based on screen height.
     * Range: 40px to 60px.
     * @param height - Screen height in pixels
     * @returns Button size in pixels
     */
    static getResponsiveMuteButtonSize(height: number): number {
        return Math.max(40, Math.min(60, Math.floor(height / 10)));
    }
}
